import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useMultiCouponDialog } from '../dialog/use-multi-coupon-dialog';
import { useMultiCouponClaimedDialog } from '../dialog/use-multi-coupon-claimed-dialog';
import { useUrlCoupon } from '../hooks/use-url-coupon';
import { useMultiCouponResolver } from '../hooks/use-multi-coupon-resolver';
import { DefaultCurrencyCode, DEFAULT_ACTIVITY } from '../helpers';
import { CouponDetailsProps } from '../coupon-props';

export const MultiCouponSelector = (props) => {
  const {
    activity,
    currentCoupon,
    updateCoupon,
    getCouponCurrencyCode,
  } = props;

  const { urlCoupon, isValidUrlCoupon } = useUrlCoupon(activity);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [
    newCoupon,
    fetchNewCouponDetails,
    resetNewCoupon,
  ] = useMultiCouponResolver(activity);

  const isValidNewCoupon = !_.isEmpty(_.get(newCoupon, 'couponDetails'));
  const isCurrentCouponResolved = !_.isEmpty(_.get(currentCoupon, 'couponDetails'));
  const currentCouponString = _.get(currentCoupon, ['couponValue', 'couponString'], '');

  const currentMultiCoupon = useMemo(() => {
    if (isCurrentCouponResolved &&
      !currentCoupon.isCouponExpired &&
      currentCoupon.isCouponValid) {
      return {
        couponString: currentCouponString,
        couponStringType: currentCoupon.couponValue.couponStringType || '',
        couponDetails: currentCoupon.couponDetails || null,
      };
    }
    return null;
  }, [
    currentCouponString,
    currentCoupon.couponValue,
    currentCoupon.couponDetails,
    currentCoupon.isCouponExpired,
    currentCoupon.isCouponValid,
    isCurrentCouponResolved]);

  const canShowMultiCouponDialog = useMemo(() => (
    // Can show multi coupon dialog when there is valid primary coupon
    // already evaluated and valid URL coupon and new coupon details computed.
    isCurrentCouponResolved &&
    isValidNewCoupon &&
    _.isEmpty(selectedCoupon)
  ), [isCurrentCouponResolved, isValidNewCoupon, selectedCoupon]);

  useEffect(() => {
    if (isCurrentCouponResolved &&
      isValidUrlCoupon &&
      _.isNull(newCoupon) &&
      _.isEmpty(selectedCoupon) &&
      !_.isEmpty(currentCouponString) &&
      (currentCouponString !== urlCoupon.couponString)) {
      if (currentCoupon.isCouponExpired || !currentCoupon.isCouponValid) {
        // If the current coupon has already expired or invalid, then we silently use the new coupon,
        // in the url.
        updateCoupon({
          couponString: urlCoupon.couponString,
          couponStringType: urlCoupon.couponStringType,
          isManual: false,
          isSetUrlCoupon: true,
        });
      } else {
        // We fetch the details of new coupon and show multi coupon dialog to the user.
        fetchNewCouponDetails(urlCoupon);
      }
    }
  }, [
    currentCouponString,
    currentCoupon.isCouponExpired,
    currentCoupon.isCouponValid,
    fetchNewCouponDetails,
    isCurrentCouponResolved,
    isValidUrlCoupon,
    newCoupon,
    selectedCoupon,
    updateCoupon,
    urlCoupon]);

  const [multiCouponClaimedDialog, showMultiCouponClaimedDialog] =
    useMultiCouponClaimedDialog({
      activity,
      coupon: selectedCoupon,
      getCouponCurrencyCode,
    });

  const processMultiCouponClaimed = useCallback((coupon, isDismiss = false) => {
    updateCoupon({
      couponString: coupon.couponString,
      couponStringType: coupon.couponStringType,
      isManual: false,
      isSetUrlCoupon: true,
    });
    resetNewCoupon();
    setSelectedCoupon(coupon);
    if (!isDismiss) {
      showMultiCouponClaimedDialog();
    }
  }, [updateCoupon, resetNewCoupon, showMultiCouponClaimedDialog]);

  const [multiCouponDialog, showMultiCouponDialog] = useMultiCouponDialog({
    activity,
    newCoupon,
    oldCoupon: currentMultiCoupon,
    currencyCode: DefaultCurrencyCode,
    onCouponSelected: processMultiCouponClaimed,
    getCouponCurrencyCode,
  });

  useEffect(() => {
    if (canShowMultiCouponDialog) {
      showMultiCouponDialog();
    }
  }, [canShowMultiCouponDialog, showMultiCouponDialog]);

  return (
    <React.Fragment>
      {multiCouponDialog}
      {multiCouponClaimedDialog}
    </React.Fragment>
  );
};

MultiCouponSelector.propTypes = {
  activity: PropTypes.shape({}),
  currentCoupon: PropTypes.shape({
    isCouponExpired: PropTypes.bool.isRequired,
    isCouponValid: PropTypes.bool.isRequired,
    couponValue: PropTypes.shape({
      couponString: PropTypes.string.isRequired,
      couponStringType: PropTypes.string.isRequired,
    }),
    couponDetails: CouponDetailsProps,
  }),
  updateCoupon: PropTypes.func.isRequired,
  getCouponCurrencyCode: PropTypes.func,
};

MultiCouponSelector.defaultProps = {
  activity: DEFAULT_ACTIVITY,
  currentCoupon: null,
  getCouponCurrencyCode: _.noop,
};
